/*
Badge evaluator — pure function that turns a snapshot of (captures,
arbresById, speciesInfo) into a BadgeState[] in the same order as the
catalogue.

Strategy mirrors Android BadgeEvaluator.kt: single chronological pass
over captures, accumulating count / distinct species / distinct
remarquables / year-month set / season-set. Each criterion's
`unlockedAt` is the timestamp of the capture that flipped the threshold,
so the badges screen can show "Débloqué le …" without a second pass.
*/

import { kBadgeCatalog, type BadgeDef, type BadgeState } from './badge'
import type { Capture } from './types'
import type { ArbreFeature } from './arbresFeatures'
import type { ArbreDetails } from './arbresDetails'
import type { SpeciesEntry } from './speciesInfo'

export interface EvaluatorInput
{
	captures: Capture[]
	arbresById: Map<number, ArbreFeature>  // for the remarquable check; only the ids we need are queried
	detailsById: Map<number, ArbreDetails>  // for hauteur/circ/arr-based criteria (Géant / Vieux sage / Tourneur / Tour complet)
	speciesInfo: Map<number, SpeciesEntry>  // for the espece-rare threshold (count < 100)
}

const kEspeceRareThreshold: number = 100  // species count below which it counts as "rare"
const kRondeDesSaisonsTarget: number = 4  // need a capture in each of the 4 seasons
const kAnneeCompleteWindow: number = 12  // 12 consecutive year-months
const kGeantHeightMeters: number = 30  // hauteur > kGeantHeightMeters → Géant
const kVieuxSageCircCm: number = 400  // circonférence > kVieuxSageCircCm → Vieux sage (4 m)
const kTourneurArrTarget: number = 10  // distinct arrondissements for Tourneur de Paris
const kTourCompletArrTarget: number = 20  // 20 arrondissements for Tour complet

/*
Evaluate every badge in catalogue order. Each output entry has the same
def reference (so React-style identity holds) and a possibly-null
unlockedAt timestamp. Captures must already be sorted oldest-first;
that's the contract `listCaptures()` provides.
*/
export function evaluateBadges(inInput: EvaluatorInput): BadgeState[]
{
	const vAcc = newAccumulator()  // running stats updated capture-by-capture
	const vUnlocks: Map<string, number> = new Map()  // badgeId -> first timestamp that unlocked it

	for (const vCap of inInput.captures)
	{
		vAcc.count += 1
		vAcc.species.add(vCap.speciesIndex)
		vAcc.seasons.add(vCap.season)
		vAcc.yearMonths.add(yearMonthKey(vCap.timestamp))
		const vArbre = inInput.arbresById.get(vCap.arbreId)  // for remarquable check; null is fine
		if (vArbre?.remarquable) vAcc.remarquables.add(vCap.arbreId)

		// Per-arbre details — sourced from the lazy-loaded details file.
		// May be absent for very recent CSV rows or if the details file
		// hasn't yet been fetched; null is treated as "no information".
		const vDetails = inInput.detailsById.get(vCap.arbreId)
		if (vDetails)
		{
			if (vDetails.arrondissement) vAcc.arrondissements.add(vDetails.arrondissement)
			if (typeof vDetails.heightMeters === 'number' && vDetails.heightMeters > kGeantHeightMeters) vAcc.geantSeen = true
			if (typeof vDetails.circumferenceCm === 'number' && vDetails.circumferenceCm > kVieuxSageCircCm) vAcc.vieuxSageSeen = true
		}

		const vRareSpeciesNow = isRareSpecies(vCap.speciesIndex, inInput.speciesInfo)  // does THIS capture's species qualify
		if (vRareSpeciesNow) vAcc.rareSpeciesEverSeen = true

		// Threshold checks — record the timestamp the FIRST time the
		// criterion is satisfied. Using vUnlocks.has() guard keeps the
		// "unlockedAt = capture-that-flipped" semantics.
		check(vUnlocks, 'first_capture', vAcc.count >= 1, vCap.timestamp)
		check(vUnlocks, 'promenade', vAcc.count >= 10, vCap.timestamp)
		check(vUnlocks, 'marcheur', vAcc.count >= 50, vCap.timestamp)
		check(vUnlocks, 'centurion', vAcc.count >= 100, vCap.timestamp)

		check(vUnlocks, 'botaniste_amateur', vAcc.species.size >= 50, vCap.timestamp)
		check(vUnlocks, 'botaniste_confirme', vAcc.species.size >= 200, vCap.timestamp)
		check(vUnlocks, 'espece_rare', vAcc.rareSpeciesEverSeen, vCap.timestamp)

		check(vUnlocks, 'tourneur_de_paris', vAcc.arrondissements.size >= kTourneurArrTarget, vCap.timestamp)
		check(vUnlocks, 'tour_complet', vAcc.arrondissements.size >= kTourCompletArrTarget, vCap.timestamp)

		check(vUnlocks, 'chasseur_remarquables', vAcc.remarquables.size >= 10, vCap.timestamp)
		check(vUnlocks, 'legende', vAcc.remarquables.size >= 50, vCap.timestamp)

		check(vUnlocks, 'ronde_des_saisons', vAcc.seasons.size >= kRondeDesSaisonsTarget, vCap.timestamp)

		if (!vUnlocks.has('annee_complete') && hasAnneeComplete(vAcc.yearMonths))
		{
			vUnlocks.set('annee_complete', vCap.timestamp)
		}

		check(vUnlocks, 'geant', vAcc.geantSeen, vCap.timestamp)
		check(vUnlocks, 'vieux_sage', vAcc.vieuxSageSeen, vCap.timestamp)
	}

	return kBadgeCatalog.map((inDef): BadgeState => ({
		def: inDef,
		unlockedAt: vUnlocks.get(inDef.id) ?? null,
	}))
}

interface Accumulator
{
	count: number
	species: Set<number>
	seasons: Set<number>
	yearMonths: Set<string>  // 'YYYY-MM' keys for the 12-month window check
	remarquables: Set<number>  // arbreIds of captured remarquable trees
	arrondissements: Set<string>  // distinct "8e" / "Bois de Vincennes" / etc. across captured arbres
	rareSpeciesEverSeen: boolean  // sticky once a rare species is captured
	geantSeen: boolean  // sticky once a tree taller than kGeantHeightMeters is captured
	vieuxSageSeen: boolean  // sticky once a tree thicker than kVieuxSageCircCm is captured
}

function newAccumulator(): Accumulator
{
	return {
		count: 0,
		species: new Set(),
		seasons: new Set(),
		yearMonths: new Set(),
		remarquables: new Set(),
		arrondissements: new Set(),
		rareSpeciesEverSeen: false,
		geantSeen: false,
		vieuxSageSeen: false,
	}
}

/*
Helper: record the first timestamp at which a criterion becomes true.
Idempotent — once a badge is unlocked, subsequent calls do nothing.
*/
function check(inMap: Map<string, number>, inBadgeId: string, inOk: boolean, inTs: number): void
{
	if (!inOk) return
	if (inMap.has(inBadgeId)) return
	inMap.set(inBadgeId, inTs)
}

/*
"YYYY-MM" key for a timestamp, in the user's local zone. Using local zone
matches Android's Europe/Paris assumption for the badge logic — both
platforms are personal-use and the user is in Paris.
*/
function yearMonthKey(inTs: number): string
{
	const vDate = new Date(inTs)
	const vY = vDate.getFullYear()
	const vM = String(vDate.getMonth() + 1).padStart(2, '0')
	return `${vY}-${vM}`
}

/*
True if `inMonths` contains 12 consecutive YYYY-MM keys somewhere. We
sort the unique keys, walk the run, and reset on any gap. Cheap; the
month-key set is at most ~years-of-use × 12 entries.
*/
function hasAnneeComplete(inMonths: Set<string>): boolean
{
	if (inMonths.size < kAnneeCompleteWindow) return false
	const vSorted = [...inMonths].sort()
	let vRun = 1  // number of consecutive months in the current run
	for (let vI = 1; vI < vSorted.length; vI++)
	{
		if (isNextMonth(vSorted[vI - 1], vSorted[vI])) vRun += 1
		else vRun = 1
		if (vRun >= kAnneeCompleteWindow) return true
	}
	return false
}

function isNextMonth(inA: string, inB: string): boolean
{
	const [vYa, vMa] = inA.split('-').map(Number)  // [year, month] of the earlier key
	const [vYb, vMb] = inB.split('-').map(Number)  // [year, month] of the later key
	const vNextM = vMa === 12 ? 1 : vMa + 1
	const vNextY = vMa === 12 ? vYa + 1 : vYa
	return vYb === vNextY && vMb === vNextM
}

function isRareSpecies(inSk: number, inInfo: Map<number, SpeciesEntry>): boolean
{
	const vEntry = inInfo.get(inSk)  // species-info row, may be missing for fresh dataset entries
	if (!vEntry) return false
	return vEntry.stats.count < kEspeceRareThreshold
}

/*
Convenience export — UI uses this to know "Implemented yet?" without
reaching back into the catalogue.
*/
export function isImplemented(inDef: BadgeDef): boolean { return inDef.implemented }
